/**
 * ACTIVITY-CORE — textos humanos del feed (i18n), variante POS.
 *
 * App-SPECIFIC part: the entity_type+action → phrase map. The
 * structure (patch, grouping, offline) lives in @malla/shared.
 * Message descriptors `msg` (not strings) are returned so the language is reactive.
 *
 * SEMANTIC BUSINESS ACTIONS are derived from the patch (canon
 * activity-text pattern): "voided the order", "applied a discount of X",
 * "reopened the order", "sent the order", "closed the cashbox"… with the reason
 * (`void_reason`/`discount_reason`/`reason`) cuando existe.
 */
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { type ActivityRow, formatMoney, parseActivityPatch } from "@malla/shared";

interface ActivitySentence {
  /** Sentence with `{name}` (and optionally `{amount}`) interpolated.*/
  named: MessageDescriptor;
  /** Sentence without a name (the patch does not contain it).*/
  anonymous: MessageDescriptor;
}

/**
 * Fine semantic key: refines `created`/`updated`/`deleted` with what actually
 * happened in the POS (void, discount, reopen, send to kitchen, open/
 * cerrar caja…) mirando el patch.
 */
export function activitySemanticKey(row: ActivityRow): string {
  const action = row.action ?? "updated";
  const patch = parseActivityPatch(row.patch);

  if (row.entity_type === "order") {
    const { status } = patch;
    if (status) {
      if (status.new === "sent") {
        return "sent_kitchen";
      }
      if (status.new === "paid") {
        return "order_paid";
      }
      if (status.new === "void") {
        return "order_voided";
      }
      if (status.new === "open" && (status.old === "paid" || status.old === "void")) {
        return "order_reopened";
      }
    }
    if (patch.discount) {
      const value = typeof patch.discount.new === "number" ? patch.discount.new : 0;
      return value > 0 ? "discount_applied" : "discount_removed";
    }
    if (patch.table_id && action === "updated") {
      return "order_moved_table";
    }
  }

  if (row.entity_type === "order_line" && action === "deleted") {
    // Line soft-delete = void (with or without reason); full snapshot
    // (legacy hard delete) = purge.
    return patch.deleted_at ? "line_voided" : "purged";
  }
  if (row.entity_type === "order_line" && action === "restored") {
    return "line_restored";
  }

  if (row.entity_type === "payment" && action === "created") {
    return "payment_taken";
  }
  if (row.entity_type === "payment" && action === "deleted") {
    return "payment_voided";
  }

  if (row.entity_type === "register_session") {
    if (action === "created") {
      return "register_opened";
    }
    if (patch.status?.new === "closed") {
      return "register_closed";
    }
  }

  if (row.entity_type === "cash_movement" && action === "created") {
    const kind = patch.kind?.new;
    if (kind === "in") {
      return "cash_in";
    }
    if (kind === "out") {
      return "cash_out";
    }
    if (kind === "refund") {
      return "cash_refund";
    }
  }

  if (action === "deleted") {
    // deleted_at transition = trash; full snapshot = permanent purge.
    return patch.deleted_at ? "trashed" : "purged";
  }
  return action;
}

// ─── Phrases by entity ──────────────────────────────────────────────────────

const ORDER_TEXTS: Record<string, ActivitySentence> = {
  created: { named: msg`opened order "{name}"`, anonymous: msg`opened an order` },
  sent_kitchen: {
    named: msg`sent the order "{name}" to the kitchen`,
    anonymous: msg`sent the order to the kitchen`,
  },
  order_paid: { named: msg`charged the order "{name}"`, anonymous: msg`charged the order` },
  order_voided: { named: msg`voided the order "{name}"`, anonymous: msg`voided the order` },
  order_reopened: {
    named: msg`reopened the order "{name}"`,
    anonymous: msg`reopened the order`,
  },
  discount_applied: {
    named: msg`applied a discount of {amount}`,
    anonymous: msg`applied a discount`,
  },
  discount_removed: {
    named: msg`removed the discount`,
    anonymous: msg`removed the discount`,
  },
  order_moved_table: {
    named: msg`moved the order to another table`,
    anonymous: msg`moved the order to another table`,
  },
  updated: { named: msg`edited the order "{name}"`, anonymous: msg`edited an order` },
  trashed: { named: msg`deleted the order "{name}"`, anonymous: msg`deleted an order` },
  purged: {
    named: msg`permanently deleted the order "{name}"`,
    anonymous: msg`permanently deleted an order`,
  },
  restored: { named: msg`restored the order "{name}"`, anonymous: msg`restored an order` },
  reverted: {
    named: msg`undid a change on the order "{name}"`,
    anonymous: msg`undid a change on an order`,
  },
};

const ORDER_LINE_TEXTS: Record<string, ActivitySentence> = {
  created: { named: msg`added "{name}" to the order`, anonymous: msg`added an item` },
  line_voided: { named: msg`voided the item "{name}"`, anonymous: msg`voided an item` },
  line_restored: { named: msg`restored the item "{name}"`, anonymous: msg`restored an item` },
  updated: { named: msg`edited the item "{name}"`, anonymous: msg`edited an item` },
  purged: {
    named: msg`permanently deleted the item "{name}"`,
    anonymous: msg`permanently deleted an item`,
  },
  reverted: {
    named: msg`undid a change on the item "{name}"`,
    anonymous: msg`undid a change on an item`,
  },
};

const PAYMENT_TEXTS: Record<string, ActivitySentence> = {
  payment_taken: {
    named: msg`took a payment of {amount}`,
    anonymous: msg`took a payment`,
  },
  payment_voided: {
    named: msg`voided a payment of {amount}`,
    anonymous: msg`voided a payment`,
  },
  updated: { named: msg`edited a payment`, anonymous: msg`edited a payment` },
  trashed: { named: msg`voided a payment of {amount}`, anonymous: msg`voided a payment` },
  reverted: {
    named: msg`undid a change on a payment`,
    anonymous: msg`undid a change on a payment`,
  },
};

const REGISTER_TEXTS: Record<string, ActivitySentence> = {
  register_opened: {
    named: msg`opened the register with a float of {amount}`,
    anonymous: msg`opened the register`,
  },
  register_closed: {
    named: msg`closed the register (counted {amount})`,
    anonymous: msg`closed the register`,
  },
  updated: {
    named: msg`edited the register session`,
    anonymous: msg`edited the register session`,
  },
  reverted: {
    named: msg`undid a change on the register session`,
    anonymous: msg`undid a change on the register session`,
  },
};

const CASH_MOVEMENT_TEXTS: Record<string, ActivitySentence> = {
  cash_in: { named: msg`added {amount} to the till`, anonymous: msg`added cash to the till` },
  cash_out: {
    named: msg`took {amount} out of the till`,
    anonymous: msg`took cash out of the till`,
  },
  cash_refund: { named: msg`refunded {amount}`, anonymous: msg`made a refund` },
  created: { named: msg`recorded a till movement`, anonymous: msg`recorded a till movement` },
  updated: { named: msg`edited a till movement`, anonymous: msg`edited a till movement` },
  trashed: { named: msg`voided a till movement`, anonymous: msg`voided a till movement` },
  reverted: {
    named: msg`undid a change on a till movement`,
    anonymous: msg`undid a change on a till movement`,
  },
};

/** Generic catalog/master phrases with {name}. */
const GENERIC_TEXTS: Record<string, ActivitySentence> = {
  created: { named: msg`created "{name}"`, anonymous: msg`created an item` },
  updated: { named: msg`edited "{name}"`, anonymous: msg`edited an item` },
  trashed: {
    named: msg`moved "{name}" to the trash`,
    anonymous: msg`moved an item to the trash`,
  },
  purged: {
    named: msg`permanently deleted "{name}"`,
    anonymous: msg`permanently deleted an item`,
  },
  restored: { named: msg`restored "{name}"`, anonymous: msg`restored an item` },
  deleted: {
    named: msg`deleted "{name}"`,
    anonymous: msg`deleted an item`,
  },
  reverted: {
    named: msg`undid a change on "{name}"`,
    anonymous: msg`undid a change`,
  },
};

const FALLBACK: ActivitySentence = {
  named: msg`changed "{name}"`,
  anonymous: msg`made a change`,
};

/** Visible entity name in the feed (i18n): "product", "table"…*/
export const ENTITY_LABELS: Record<string, MessageDescriptor> = {
  menu_category: msg`menu category`,
  menu_product: msg`product`,
  modifier_group: msg`modifier group`,
  modifier_option: msg`modifier option`,
  product_modifier_group: msg`product modifier link`,
  branch: msg`branch`,
  dining_table: msg`table`,
  product_variant: msg`variant`,
  combo: msg`combo`,
  combo_item: msg`combo item`,
  promotion: msg`promotion`,
  customer: msg`customer`,
  ingredient: msg`ingredient`,
  recipe_item: msg`recipe line`,
  supplier: msg`supplier`,
  employee: msg`employee`,
  order: msg`order`,
  order_line: msg`order item`,
  order_line_modifier: msg`item modifier`,
  payment: msg`payment`,
  register_session: msg`register session`,
  cash_movement: msg`till movement`,
  stock_movement: msg`stock movement`,
  purchase_order: msg`purchase order`,
  purchase_order_line: msg`purchase line`,
  loyalty_movement: msg`loyalty movement`,
  reservation: msg`reservation`,
};

/** Visible name of entity extracted from patch (name/customer_name, new or old). */
export function activityEntityName(row: ActivityRow): string | null {
  const patch = parseActivityPatch(row.patch);
  for (const key of ["name", "customer_name", "note"] as const) {
    const change = patch[key];
    if (!change) {
      continue;
    }
    const value = change.new ?? change.old;
    if (typeof value === "string" && value.trim().length > 0) {
      return value;
    }
  }
  return null;
}

/** Relevant amount for the entry (cents) according to its semantic key. */
export function activityAmountCents(row: ActivityRow, key: string): number | null {
  const patch = parseActivityPatch(row.patch);
  const pick = (field: string): number | null => {
    const change = patch[field];
    const value = change?.new ?? change?.old;
    return typeof value === "number" ? value : null;
  };
  switch (key) {
    case "discount_applied": {
      return pick("discount");
    }
    case "payment_taken":
    case "payment_voided": {
      return pick("amount");
    }
    case "register_opened": {
      return pick("opening_float");
    }
    case "register_closed": {
      return pick("counted_total");
    }
    case "cash_in":
    case "cash_out":
    case "cash_refund": {
      return pick("amount");
    }
    default: {
      return null;
    }
  }
}

/** Business reason for the entry (void/discount/movement), if present. */
export function activityReason(row: ActivityRow): string | null {
  const patch = parseActivityPatch(row.patch);
  for (const key of ["void_reason", "discount_reason", "reason"] as const) {
    const change = patch[key];
    const value = change?.new ?? null;
    if (typeof value === "string" && value.trim().length > 0) {
      return value;
    }
  }
  return null;
}

const TEXTS_BY_ENTITY: Record<string, Record<string, ActivitySentence>> = {
  order: ORDER_TEXTS,
  order_line: ORDER_LINE_TEXTS,
  payment: PAYMENT_TEXTS,
  register_session: REGISTER_TEXTS,
  cash_movement: CASH_MOVEMENT_TEXTS,
};

/**
 * i18n phrase for an entry: descriptor + values. Render with
 * `t(descriptor, values)` (o i18n._) en el componente.
 */
export function activitySentence(row: ActivityRow): {
  descriptor: MessageDescriptor;
  values: Record<string, string>;
} {
  const key = activitySemanticKey(row);
  const table = TEXTS_BY_ENTITY[row.entity_type ?? ""] ?? GENERIC_TEXTS;
  const sentence = table[key] ?? GENERIC_TEXTS[key] ?? FALLBACK;

  const values: Record<string, string> = {};
  const name = activityEntityName(row);
  if (name) {
    values.name = name;
  }
  const amount = activityAmountCents(row, key);
  if (amount !== null) {
    values.amount = formatMoney(amount);
  }

  // `named` requires the interpolated value; if missing, use anonymous variant.
  const needsName = !name && sentenceUsesName(sentence.named);
  const needsAmount = amount === null && sentenceUsesAmount(sentence.named);
  const descriptor = needsName || needsAmount ? sentence.anonymous : sentence.named;
  return { descriptor, values };
}

function sentenceUsesName(descriptor: MessageDescriptor): boolean {
  // oxlint-disable-next-line lingui/no-unlocalized-strings -- ICU placeholder token, not UI text
  return (descriptor.message ?? descriptor.id ?? "").includes("{name}");
}

function sentenceUsesAmount(descriptor: MessageDescriptor): boolean {
  // oxlint-disable-next-line lingui/no-unlocalized-strings -- ICU placeholder token, not UI text
  return (descriptor.message ?? descriptor.id ?? "").includes("{amount}");
}

const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/** "5 min ago" localized with Intl; falls back to day/date for older items.*/
export function formatRelativeTime(
  iso: string | null,
  locale: string,
  now: Date = new Date(),
): string {
  if (!iso) {
    return "";
  }
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) {
    return "";
  }
  const diff = ts - now.getTime();
  const abs = Math.abs(diff);
  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
    if (abs < MINUTE_MS) {
      return rtf.format(Math.round(diff / SECOND_MS), "second");
    }
    if (abs < HOUR_MS) {
      return rtf.format(Math.round(diff / MINUTE_MS), "minute");
    }
    if (abs < DAY_MS) {
      return rtf.format(Math.round(diff / HOUR_MS), "hour");
    }
    if (abs < 7 * DAY_MS) {
      return rtf.format(Math.round(diff / DAY_MS), "day");
    }
    return new Date(ts).toLocaleDateString(locale);
  } catch {
    return new Date(ts).toLocaleString();
  }
}
